import { useEffect, useRef, useState } from 'react';

type AnimationType = 'fade-in' | 'slide-up' | 'slide-up-sm' | 'slide-left' | 'slide-right' | 'scale-in';

interface AnimatedSectionProps {
  children: React.ReactNode;
  animation?: AnimationType;
  delay?: number;
  threshold?: number;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  children,
  animation = 'slide-up',
  delay = 0,
  threshold = 0.12,
  className = '',
  as: Tag = 'div',
}) => {
  //#region Declare State
  const ref = useRef<Element>(null);
  const [isVisible, setIsVisible] = useState(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  //#endregion Implement Hook

  const animationStyle: React.CSSProperties = delay > 0 ? { animationDelay: `${delay}ms` } : {};

  const visibilityClass = isVisible ? `animate-${animation}` : 'opacity-0';

  const Component = Tag as React.ElementType;

  return (
    <Component
      ref={ref}
      className={`${visibilityClass} ${className}`}
      style={animationStyle}
    >
      {children}
    </Component>
  );
};

export default AnimatedSection;
